import type { Diagnostic } from "@codemirror/lint";
import type { ScopeNode } from "@/types/bbel-types";
import { parseChain } from "./bbel-autocomplete";

export interface BbelDiagnostic {
  from: number;
  to: number;
  severity: "warning" | "error";
  message: string;
}

export interface BbelSlot {
  /** Document offset of the opening `{{`. */
  from: number;
  /** Document offset just past the closing `}}`. */
  to: number;
  /** Full slot text including delimiters: `{{ org.id }}`. */
  text: string;
}

/**
 * Structural BBEL checks (#5683) — instant, client-side, never wrong-reject: an unclosed `{{` and an
 * empty slot. Semantic checks (unknown scope member / function, banned grammar, parse errors) are
 * authoritative and run server-side via `POST /expressions/validate`, so the client holds no copy of
 * the scope walk (one implementation, no drift). Naive `{{`/`}}` scanning matches the server lexer for
 * the common case; a `}}` inside a slot's string literal is an accepted edge case.
 */
export function structuralDiagnostics(text: string): BbelDiagnostic[] {
  const diagnostics: BbelDiagnostic[] = [];
  let i = 0;
  while (i < text.length) {
    const open = text.indexOf("{{", i);
    if (open < 0) break;
    const close = text.indexOf("}}", open + 2);
    if (close < 0) {
      diagnostics.push({
        from: open,
        to: text.length,
        severity: "error",
        message: "Unclosed '{{' — every expression slot must end with '}}'.",
      });
      break;
    }
    if (text.slice(open + 2, close).trim() === "")
      diagnostics.push({ from: open, to: close + 2, severity: "warning", message: "Empty expression slot." });
    i = close + 2;
  }
  return diagnostics;
}

/**
 * The balanced, non-empty `{{ … }}` slots in a field, each with its document range and full text. Feeds
 * the server validator index-aligned; empty and unclosed slots are handled by
 * {@link structuralDiagnostics} and excluded here.
 */
export function extractSlots(text: string): BbelSlot[] {
  const slots: BbelSlot[] = [];
  let i = 0;
  while (i < text.length) {
    const open = text.indexOf("{{", i);
    if (open < 0) break;
    const close = text.indexOf("}}", open + 2);
    if (close < 0) break;
    const to = close + 2;
    if (text.slice(open + 2, close).trim() !== "")
      slots.push({ from: open, to, text: text.slice(open, to) });
    i = to;
  }
  return slots;
}

export function toCmDiagnostic(d: BbelDiagnostic): Diagnostic {
  return { from: d.from, to: d.to, severity: d.severity, message: d.message };
}

/**
 * Advisory member warnings (#5683): a path segment that isn't among a level's KNOWN children gets a
 * warning squiggle — `output[0].fffff` when the sampled shape has no `fffff`. Deliberately bounded
 * so it can never wrong-reject:
 *
 * - Only levels the tree knows (children present) and only below OPEN nodes — closed levels
 *   (wizard, org, service ids) are member-checked server-side as errors, so they're skipped here.
 * - The segment under the caret that prefixes a known member ("WBSNum" while "WBSNumber" exists)
 *   is treated as typing-in-progress, not flagged. The exemption is caret-scoped: the same wrong
 *   prefix anywhere the caret isn't — a committed `BillWB` — warns like any other unknown member.
 * - Warning severity, never error: the sample is one run's shape, not a contract — publish and the
 *   server validator are unaffected.
 */
export function advisoryMemberDiagnostics(text: string, roots: ScopeNode[], caret?: number): BbelDiagnostic[] {
  if (roots.length === 0) return [];
  const diagnostics: BbelDiagnostic[] = [];

  for (const slot of extractSlots(text)) {
    const inner = slot.text.slice(2, -2);
    const innerBase = slot.from + 2;
    for (const match of inner.matchAll(/[A-Za-z_$][\w$.[\]"']*/g)) {
      const chain = match[0];
      const segments = fullSegments(chain);
      if (!segments) continue;

      let level: ScopeNode[] = roots;
      let parent: ScopeNode | undefined;
      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        const node = level.find((n) => n.segment === segment);
        if (!node) {
          const knowable = parent !== undefined && !parent.closed;
          const range = segmentRange(chain, segments, i);
          const from = innerBase + match.index + range.from;
          const to = innerBase + match.index + range.to;
          const caretHere = caret !== undefined && caret >= from && caret <= to;
          const inProgress = caretHere && level.some((n) => n.segment.startsWith(segment));
          if (knowable && !inProgress) {
            const staleHint = parent!.path.includes(".output")
              ? " If the shape changed, re-run the workflow to refresh the sample."
              : "";
            diagnostics.push({
              from,
              to,
              severity: "warning",
              message: `'${segment}' isn't a known member of '${parent!.path}'.${staleHint}`,
            });
          }
          break;
        }
        // A locked member exists but never resolves (secret connection fields, #6010) — using one
        // is deterministic breakage, so it's an error even though the advisory layer stays warning
        // for shape guesses. The server publish gate is the follow-up (#6012).
        if (node.locked) {
          const range = segmentRange(chain, segments, i);
          diagnostics.push({
            from: innerBase + match.index + range.from,
            to: innerBase + match.index + range.to,
            severity: "error",
            message: `'${segment}' is a secret — secrets are never available in expressions, so this resolves to nothing at run time.`,
          });
          break;
        }
        if (!node.children || node.children.length === 0) break; // open below — nothing known
        parent = node;
        level = node.children;
      }
    }
  }
  return diagnostics;
}

/** Every resolved segment of the chain including a trailing complete identifier; null = not walkable. */
function fullSegments(chain: string): string[] | null {
  const parsed = parseChain(chain) ?? parseChain(`${chain}.`);
  if (!parsed) return null;
  return parsed.partial ? [...parsed.segments, parsed.partial] : parsed.segments;
}

/** Char range of segment `index` within the chain — the squiggle lands on the offending segment. */
function segmentRange(chain: string, segments: string[], index: number): { from: number; to: number } {
  let cursor = 0;
  for (let i = 0; i <= index; i++) {
    // Quoted-key segments are stored unquoted but appear verbatim inside the brackets, so indexOf
    // finds them; hops (`[0]`) and identifiers match literally.
    const at = chain.indexOf(segments[i], cursor);
    if (at < 0) return { from: 0, to: chain.length }; // fallback: whole chain
    if (i === index) return { from: at, to: at + segments[i].length };
    cursor = at + segments[i].length;
  }
  return { from: 0, to: chain.length };
}
